import { exportToCSV, exportToPDF } from '../../../../utils/exportUtils';
import AppCard from '../../../../components/ui/AppCard';
import React, { useState, useRef, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toast } from 'primereact/toast';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { getBeneficiaryChangeRequests, approveBeneficiaryChangeRequest, rejectBeneficiaryChangeRequest } from '../../../../services/beneficiary-service';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

const BeneficiaryRequestsTable = () => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [reviewDialogVisible, setReviewDialogVisible] = useState(false);
    const [detailsDialogVisible, setDetailsDialogVisible] = useState(false);
    const [actionType, setActionType] = useState(null); // 'APPROVE' or 'REJECT'
    const [comments, setComments] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [globalFilter, setGlobalFilter] = useState('');

    const toast = useRef(null);
    const dt = useRef(null);


  const globalFilterElement = (
      <div className="relative w-full max-w-xs">
          <i className="pi pi-search absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50"></i>
          <input
              type="text"
              className="input input-bordered w-full pl-10 h-10 shadow-sm bg-base-100 rounded-full"
              placeholder="Search..."
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
          />
      </div>
  );

  const header = (
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-center w-full">
          {globalFilterElement}
          <div className="flex items-center gap-3 ml-auto">
              <div className="dropdown dropdown-end">
                  <div tabIndex={0} role="button" className="btn btn-outline btn-ghost h-10 min-h-0 shadow-sm rounded-full">
                      <i className="pi pi-file-export"></i> Export
                  </div>
                  <ul tabIndex={0} className="dropdown-content z-[99] menu p-2 shadow-sm bg-base-100 border border-base-200 rounded-box w-52 mt-2">
                                    <li><button type="button" onClick={() => exportToCSV(dt, 'Beneficiary Requests')}><i className="pi pi-file-excel text-success"></i> Download CSV</button></li>
                                    <li><button type="button" onClick={() => exportToPDF(dt, 'Beneficiary Requests', 'Beneficiary Requests Report')}><i className="pi pi-file-pdf text-error"></i> Download PDF</button></li>
                                </ul>
              </div>
          </div>
      </div>
  );


    useEffect(() => {
        fetchRequests();
    }, []);

    const fetchRequests = async () => {
        setLoading(true);
        try {
            const response = await getBeneficiaryChangeRequests();
            if (response.data.status === "SUCCESS" || response.data.status === "success" || Array.isArray(response.data.responseBody)) {
                setRequests(response.data.responseBody || response.data);
            } else {
                setRequests([]);
            }
        } catch (error) {
            console.error('Error fetching beneficiary requests:', error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.response?.status === 403 
                        ? 'Forbidden: You do not have permission to view change requests.' 
                        : 'Failed to load change requests.'
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleActionOpen = (request, type) => {
        setSelectedRequest(request);
        setActionType(type);
        setComments('');
        setReviewDialogVisible(true);
    };

    const handleReviewSubmit = async () => {
        if (!selectedRequest) return;

        // Validation: comment is required for rejection
        if (actionType === 'REJECT' && !comments.trim()) {
            if (toast.current) {
                toast.current.show({
                    severity: 'warn',
                    summary: 'Validation Error',
                    detail: 'Please provide a reason or comments for rejecting this request.'
                });
            }
            return;
        }

        setSubmitting(true);
        const payload = {
            comments: comments,
            reason: comments,
            reviewerSignature: 'System Reviewer'
        };

        try {
            let response;
            const reqId = selectedRequest.requestId || selectedRequest.id;
            if (actionType === 'APPROVE') {
                response = await approveBeneficiaryChangeRequest(reqId, payload);
            } else {
                response = await rejectBeneficiaryChangeRequest(reqId, payload);
            }

            if (toast.current) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.data.message || `Request successfully ${actionType.toLowerCase()}d.`
                });
            }
            setReviewDialogVisible(false);
            fetchRequests(); // Invalidate and refresh the list
        } catch (error) {
            console.error(`Error performing ${actionType} action:`, error);
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Action Failed',
                    detail: error.response?.status === 403 
                        ? 'Forbidden: You are not authorized to review requests.' 
                        : (error.response?.data?.message || 'Server error occurred during processing.')
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    const resolveStatus = (rowData) => {
        if (rowData.approved) return 'APPROVED';
        if (rowData.rejected) return 'REJECTED';
        return 'PENDING';
    };

    const getStatusSeverity = (status) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED':
                return 'success';
            case 'REJECTED':
                return 'danger';
            case 'PENDING':
            default:
                return 'warning';
        }
    };

    const renderStatus = (rowData) => {
        const status = resolveStatus(rowData);
        return <Tag value={status} severity={getStatusSeverity(status)} />;
    };

    
  const renderActions = (rowData) => {
        const isPending = !rowData.approved && !rowData.rejected;
        return (
            <div className="actions-container flex items-center justify-center gap-2">
                <div className="tooltip" data-tip="View Details">
                    <button
                        className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                        onClick={() => {
                            setSelectedRequest(rowData);
                            setDetailsDialogVisible(true);
                        }}
                    >
                        <i className="pi pi-eye text-lg"></i>
                    </button>
                </div>
                {isPending && (
                    <>
                        <div className="tooltip" data-tip="Approve">
                            <button
                                className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                                onClick={() => handleActionOpen(rowData, 'APPROVE')}
                            >
                                <i className="pi pi-check text-lg"></i>
                            </button>
                        </div>
                        <div className="tooltip" data-tip="Reject">
                            <button
                                className="btn btn-sm btn-circle btn-ghost text-base-content/70 hover:text-base-content hover:bg-base-200"
                                onClick={() => handleActionOpen(rowData, 'REJECT')}
                            >
                                <i className="pi pi-times text-lg"></i>
                            </button>
                        </div>
                    </>
                )}
            </div>
        );
    };

    const renderBeneficiaryCard = (title, beneficiary) => {
        if (!beneficiary) {
            return (
                <div className="p-card p-3 h-full bg-light border-round">
                    <h5 className="font-semibold text-muted mb-2">{title}</h5>
                    <p className="text-muted">None / No beneficiary configured</p>
                </div>
            );
        }

        return (
            <div className="p-card p-3 h-full border-1 border-300 border-round">
                <h5 className="font-semibold mb-3 text-primary">{title}</h5>
                <div className="grid">
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">FIRST NAME</strong>
                        <span>{beneficiary.firstName || beneficiary.First_Name || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">LAST NAME</strong>
                        <span>{beneficiary.lastName || beneficiary.Surname || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">NATIONAL ID / ID NUMBER</strong>
                        <span>{beneficiary.idNumber || beneficiary.ID_Number || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">RELATIONSHIP</strong>
                        <span>{beneficiary.relationship || beneficiary.Relationship || 'N/A'}</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">PERCENTAGE SHARE</strong>
                        <span>{beneficiary.share || beneficiary.percentage || beneficiary.Percentage || 'N/A'}%</span>
                    </div>
                    <div className="col-12 md:col-6 mb-2">
                        <strong className="text-muted block text-xs">PHONE NUMBER</strong>
                        <span>{beneficiary.phoneNumber || beneficiary.Home_Tel || 'N/A'}</span>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full">
            <Toast ref={toast} />

            <AppCard title="Beneficiary Change Requests">
                {loading ? (
                    <div className="p-4">
                        <Skeleton className="mb-2" height="2rem"></Skeleton>
                        <Skeleton className="mb-2" height="2rem"></Skeleton>
                        <Skeleton className="mb-2" height="2rem"></Skeleton>
                        <Skeleton className="mb-2" height="2rem"></Skeleton>
                    </div>
                ) : requests.length === 0 ? (
                    <div className="text-center p-5">
                        <i className="pi pi-inbox text-muted" style={{ fontSize: '3rem' }}></i>
                        <h4 className="mt-3 text-muted">No change requests found</h4>
                        <p className="text-muted">Currently, there are no pending beneficiary change requests to review.</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <DataTable
                            ref={dt}
                            header={header}
                            paginatorTemplate="CurrentPageReport RowsPerPageDropdown PrevPageLink PageLinks NextPageLink"
                            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
                            value={requests}
                            paginator
                            rows={10}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            globalFilter={globalFilter}
                            emptyMessage="No matching requests found"
                            tableStyle={{ minWidth: '60rem' }}
                        >
                            <Column field="policyNumber" header="Policy Number" sortable />
                            <Column field="createdAt" header="Requested Date" sortable body={(rowData) => rowData.createdAt ? new Date(rowData.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : 'N/A'} />
                            <Column field="operation" header="Type" sortable body={(rowData) => rowData.operation || 'Update'} />
                            <Column field="status" header="Status" body={renderStatus} sortable />
                            <Column header="Actions" body={renderActions} style={{ textAlign: 'center', width: '10rem' }} />
                        </DataTable>
                    </div>
                )}
            </AppCard>

            {/* Side-by-Side Details Dialog */}
            <Dialog
                header={`Beneficiary Change Details - Policy #${selectedRequest?.policyNumber}`}
                visible={detailsDialogVisible}
                style={{ width: '70vw' }}
                maximizable
                onHide={() => {
                    setDetailsDialogVisible(false);
                    setSelectedRequest(null);
                }}
            >
                {selectedRequest && (
                    <div className="p-fluid">
                        <div className="grid grid-nogutter mb-4">
                            <div className="col-12 md:col-4 mb-2">
                                <strong>Request Status: </strong>
                                <Tag className="ml-1" value={resolveStatus(selectedRequest)} severity={getStatusSeverity(resolveStatus(selectedRequest))} />
                            </div>
                            <div className="col-12 md:col-4 mb-2">
                                <strong>Request Date: </strong>
                                <span>{selectedRequest.createdAt ? new Date(selectedRequest.createdAt).toLocaleString() : 'N/A'}</span>
                            </div>
                            <div className="col-12 md:col-4 mb-2">
                                <strong>Requested Type: </strong>
                                <Tag severity="info" value={selectedRequest.operation || 'UPDATE'} />
                            </div>
                        </div>

                        <div className="grid">
                            <div className="col-12 md:col-6">
                                {renderBeneficiaryCard("CURRENT BENEFICIARY STATE (BEFORE)", selectedRequest.operation === 'ADD' ? null : selectedRequest.currentBeneficiary)}
                            </div>
                            <div className="col-12 md:col-6">
                                {renderBeneficiaryCard("PROPOSED BENEFICIARY STATE (AFTER)", selectedRequest.details)}
                            </div>
                        </div>

                        {selectedRequest.requestReason && (
                            <div className="mt-3 p-card p-3 bg-light border-round">
                                <strong className="text-muted block text-xs mb-1">REQUEST REASON</strong>
                                <p className="m-0">{selectedRequest.requestReason}</p>
                            </div>
                        )}

                        {selectedRequest.reviewReason && (
                            <div className="mt-3 p-card p-3 bg-light border-round">
                                <strong className="text-muted block text-xs mb-1 text-danger">REVIEWER COMMENTS</strong>
                                <p className="m-0">{selectedRequest.reviewReason}</p>
                            </div>
                        )}

                        {!selectedRequest.approved && !selectedRequest.rejected && (
                            <div className="flex justify-content-end gap-3 mt-4">
                                <Button
                                    label="Reject"
                                    icon="pi pi-times"
                                    className="p-button-danger p-button-outlined"
                                    onClick={() => {
                                        setDetailsDialogVisible(false);
                                        handleActionOpen(selectedRequest, 'REJECT');
                                    }}
                                />
                                <Button
                                    label="Accept"
                                    icon="pi pi-check"
                                    className="p-button-success"
                                    onClick={() => {
                                        setDetailsDialogVisible(false);
                                        handleActionOpen(selectedRequest, 'APPROVE');
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}
            </Dialog>

            {/* Approval/Rejection Comments Dialog */}
            <Dialog
                header={`${actionType === 'APPROVE' ? 'Approve' : 'Reject'} Beneficiary Change Request`}
                visible={reviewDialogVisible}
                style={{ width: '450px' }}
                modal
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            className="p-button-text"
                            disabled={submitting}
                            onClick={() => {
                                setReviewDialogVisible(false);
                                setSelectedRequest(null);
                            }}
                        />
                        <Button
                            label={actionType === 'APPROVE' ? 'Confirm Approval' : 'Confirm Rejection'}
                            icon="pi pi-check"
                            className={actionType === 'APPROVE' ? 'p-button-success' : 'p-button-danger'}
                            loading={submitting}
                            onClick={handleReviewSubmit}
                        />
                    </div>
                }
                onHide={() => {
                    setReviewDialogVisible(false);
                    setSelectedRequest(null);
                }}
            >
                <div className="p-fluid">
                    {actionType === 'REJECT' && (
                        <div className="p-message p-message-warn p-3 mb-3 border-round">
                            <i className="pi pi-exclamation-triangle mr-2"></i>
                            <strong>Warning:</strong> You are about to reject this request. A comment explaining the reason is required.
                        </div>
                    )}
                    <div className="field">
                        <label htmlFor="comments" className="font-semibold block mb-2">Reviewer Comments / Reason</label>
                        <InputTextarea
                            id="comments"
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows={5}
                            autoResize
                            placeholder={actionType === 'APPROVE' ? 'Add any optional comments here...' : 'Explain the reason for rejection here (required)...'}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default BeneficiaryRequestsTable;
